import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Music, Image, Save, Loader, Play, Pause, ChevronDown, Check } from 'lucide-react';
import FileUpload from './FileUpload';
import ContributingArtistSelector from './ContributingArtistSelector';
import PrivacySettings from './PrivacySettings';
import CopyrightSettings from './CopyrightSettings';
import { MUSIC_GENRES } from '../utils/artistTypes';
import { parseTagsSafely } from '../utils/parseTags';
import api from '../services/api';

// editingUpload: pass an existing upload record to edit it directly — skips
// the upload step, pre-fills every field, and saves in place instead of
// creating something new. Lets this same form double as the "full edit"
// modal for existing tracks (e.g. from an album's media list), not just the
// step-2 form that follows a fresh upload.
const MusicUploadModal = ({ isOpen, onClose, onSuccess, editingUpload = null }) => {
    const isEditingExisting = !!editingUpload;
    const [step, setStep] = useState(1); // 1: Upload, 2: Edit Metadata
    const [uploadedFile, setUploadedFile] = useState(null);
    const [coverImage, setCoverImage] = useState(null);
    const [metadata, setMetadata] = useState({
        title: '',
        artist: '',
        album: '',
        genre: [],
        year: String(new Date().getFullYear()),
        description: '',
        lyrics: '',
        tags: '',
        isPublic: true,
        accessLevel: 'public',
        accessPassword: '',
        accessHint: '',
        isPaid: false,
        price: '',
        currency: 'USD',
        isCopyrighted: false,
        copyrightText: ''
    });
    const [contributingArtists, setContributingArtists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [descriptionTab, setDescriptionTab] = useState('description');
    const [audioPreview, setAudioPreview] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [genreDropdownOpen, setGenreDropdownOpen] = useState(false);
    const genreDropdownRef = useRef(null);

    const genres = MUSIC_GENRES;

    // Pre-fill from the existing upload and jump straight to the metadata
    // step — mirrors how MediaDisplay.jsx's own inline edit form initializes.
    useEffect(() => {
        if (!isOpen || !editingUpload) return;

        setUploadedFile({
            id: editingUpload.id,
            url: editingUpload.file_url,
            originalName: editingUpload.original_filename || editingUpload.stored_filename
        });
        setCoverImage(editingUpload.cover_image_url ? { url: editingUpload.cover_image_url } : null);
        setMetadata({
            title: editingUpload.title || '',
            artist: editingUpload.artist || '',
            album: editingUpload.album || '',
            genre: editingUpload.genre ? editingUpload.genre.split(',').map(g => g.trim()) : [],
            year: editingUpload.year ? String(editingUpload.year) : String(new Date().getFullYear()),
            description: editingUpload.description || '',
            lyrics: editingUpload.lyrics || '',
            tags: parseTagsSafely(editingUpload.tags).join(', '),
            bpm: editingUpload.bpm || '',
            key_signature: editingUpload.key_signature || '',
            track_number: editingUpload.track_number || '',
            duration: editingUpload.duration || '',
            isPublic: editingUpload.access_level ? editingUpload.access_level === 'public' : true,
            accessLevel: editingUpload.access_level || 'public',
            accessPassword: editingUpload.access_password || '',
            accessHint: editingUpload.access_hint || '',
            isPaid: editingUpload.access_level === 'paid' || parseFloat(editingUpload.price) > 0,
            price: editingUpload.price || '',
            currency: editingUpload.currency || 'USD',
            isCopyrighted: !!editingUpload.is_copyrighted,
            copyrightText: editingUpload.copyright_text || ''
        });
        setStep(2);

        api.get(`/contributing-artists/upload/${editingUpload.id}`)
            .then(res => {
                if (res.data.success) {
                    setContributingArtists(res.data.data.map(c => ({
                        user_id: c.artist_user_id,
                        username: c.username,
                        display_name: c.display_name,
                        profile_picture_url: c.profile_picture_url
                    })));
                }
            })
            .catch(err => console.error('Error loading existing contributing artists:', err));
    }, [isOpen, editingUpload]);

    const handleAudioUpload = (uploadData) => {
        setUploadedFile(uploadData);
        // Extract title from filename if no title set
        if (!metadata.title) {
            const filename = uploadData.originalName || uploadData.filename;
            const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
            setMetadata(prev => ({
                ...prev,
                title: nameWithoutExt
            }));
        }
        setStep(2);
    };

    const handleCoverImageUpload = (uploadData) => {
        setCoverImage(uploadData);
    };

    const handleMetadataChange = (field, value) => {
        setMetadata(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const toggleGenre = (genre) => {
        setMetadata(prev => ({
            ...prev,
            genre: prev.genre.includes(genre)
                ? prev.genre.filter(g => g !== genre)
                : [...prev.genre, genre]
        }));
    };

    const removeGenre = (genreToRemove) => {
        setMetadata(prev => ({
            ...prev,
            genre: prev.genre.filter(g => g !== genreToRemove)
        }));
    };

    // Close genre dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (genreDropdownRef.current && !genreDropdownRef.current.contains(event.target)) {
                setGenreDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleAudioPreview = () => {
        if (!uploadedFile?.url) return;

        if (isPlaying) {
            if (audioPreview) {
                audioPreview.pause();
                setIsPlaying(false);
            }
        } else {
            if (audioPreview) {
                audioPreview.play();
                setIsPlaying(true);
            } else {
                const audio = new Audio(uploadedFile.url);
                audio.addEventListener('ended', () => setIsPlaying(false));
                audio.addEventListener('pause', () => setIsPlaying(false));
                audio.play();
                setAudioPreview(audio);
                setIsPlaying(true);
            }
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Prepare metadata including cover image URL if uploaded
            const updateData = {
                ...metadata,
                genre: metadata.genre.join(', '),
                tags: metadata.tags ? metadata.tags.split(',').map(tag => tag.trim()) : [],
                isPublic: metadata.accessLevel === 'public',
                price: metadata.isPaid ? metadata.price : ''
            };

            if (coverImage && (coverImage.optimizedUrl || coverImage.url)) {
                updateData.cover_image_url = coverImage.optimizedUrl || coverImage.url;
            }
            
            
            // Update the uploaded file metadata via API
            const token = localStorage.getItem('deepwave_token');
            const response = await fetch(`/api/upload/${uploadedFile.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                // Add contributing artists if any are selected
                if (contributingArtists.length > 0) {
                    try {
                        const contributingArtistIds = contributingArtists.map(artist => artist.user_id);
                        await fetch(`/api/contributing-artists/upload/${uploadedFile.id}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                contributingArtistIds
                            })
                        });
                    } catch (contributingError) {
                        console.error('Error adding contributing artists:', contributingError);
                        // Don't fail the entire upload for this
                    }
                }
                
                onSuccess?.({
                    id: uploadedFile.id,
                    file_type: 'audio',
                    title: metadata.title,
                    original_filename: uploadedFile.originalName || uploadedFile.filename,
                    cover_image_url: coverImage?.optimizedUrl || coverImage?.url || null
                });
                handleClose();
            } else {
                console.error('Failed to update metadata');
            }
        } catch (error) {
            console.error('Error saving metadata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Cleanup
        if (audioPreview) {
            audioPreview.pause();
            setAudioPreview(null);
        }
        setStep(1);
        setUploadedFile(null);
        setCoverImage(null);
        setMetadata({
            title: '',
            artist: '',
            album: '',
            genre: [],
            year: String(new Date().getFullYear()),
            description: '',
            lyrics: '',
            tags: '',
            isPublic: true,
            accessLevel: 'public',
            accessPassword: '',
            accessHint: '',
            isPaid: false,
            price: '',
            currency: 'USD',
            isCopyrighted: false,
            copyrightText: ''
        });
        setContributingArtists([]);
        setGenreDropdownOpen(false);
        setIsPlaying(false);
        setDescriptionTab('description');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white">
                        {step === 1 ? 'Upload Music' : 'Edit Track Details'}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label="Close modal"
                        className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {step === 1 ? (
                        /* Step 1: File Upload */
                        <div className="space-y-6">
                            <div className="text-center">
                                <Music className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-white mb-2">
                                    Upload Your Music Track
                                </h3>
                                <p className="text-gray-400">
                                    Supported formats: MP3, WAV, OGG, M4A (Max 50MB)
                                </p>
                            </div>

                            <FileUpload
                                type="audio"
                                onUpload={handleAudioUpload}
                                onError={(error) => console.error('Upload error:', error)}
                                maxSizeMB={50}
                                accept="audio/*"
                            />
                        </div>
                    ) : (
                        /* Step 2: Metadata Editing */
                        <div className="space-y-6">
                            {/* Audio Preview */}
                            <div className="bg-gray-800/50 rounded-lg p-4">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={handleAudioPreview}
                                        aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                                        className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-5 h-5 text-white" />
                                        ) : (
                                            <Play className="w-5 h-5 text-white ml-0.5" />
                                        )}
                                    </button>
                                    <div>
                                        <p className="text-white font-semibold">
                                            {uploadedFile?.originalName}
                                        </p>
                                        <p className="text-gray-400 text-sm">
                                            {(uploadedFile?.size / 1024 / 1024).toFixed(1)} MB
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Cover Image Upload */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-3">
                                    Cover Image
                                </label>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-24 h-24 bg-gray-800 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center">
                                            {coverImage ? (
                                                <img
                                                    src={coverImage.optimizedUrl || coverImage.url}
                                                    alt="Cover"
                                                    width="96"
                                                    height="96"
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                            ) : (
                                                <Image className="w-8 h-8 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <FileUpload
                                            type="image"
                                            onUpload={handleCoverImageUpload}
                                            onError={(error) => console.error('Cover upload error:', error)}
                                            maxSizeMB={5}
                                            accept="image/*"
                                            className="h-24"
                                            compact
                                            showPreview={false}
                                        >
                                            <span className="text-blue-400">Upload cover image</span> (optional)
                                        </FileUpload>
                                    </div>
                                </div>
                            </div>

                            {/* Track Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Track Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.title}
                                        onChange={(e) => handleMetadataChange('title', e.target.value)}
                                        className="input-field"
                                        placeholder="Enter track title"
                                        required
                                    />
                                </div>

                                {/* Artist */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Artist
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.artist}
                                        onChange={(e) => handleMetadataChange('artist', e.target.value)}
                                        className="input-field"
                                        placeholder="Artist name"
                                    />
                                </div>

                                {/* Album */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Album
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.album}
                                        onChange={(e) => handleMetadataChange('album', e.target.value)}
                                        className="input-field"
                                        placeholder="Album name"
                                    />
                                </div>

                                {/* Genre */}
                                <div className="relative" ref={genreDropdownRef}>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Genres (select multiple)
                                    </label>
                                    
                                    {/* Selected genres display */}
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {metadata.genre.map((selectedGenre) => (
                                            <span
                                                key={selectedGenre}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-full"
                                            >
                                                {selectedGenre}
                                                <button
                                                    type="button"
                                                    onClick={() => removeGenre(selectedGenre)}
                                                    aria-label={`Remove ${selectedGenre} genre`}
                                                    className="ml-1 text-blue-200 hover:text-white"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>

                                    {/* Genre dropdown trigger */}
                                    <button
                                        type="button"
                                        onClick={() => setGenreDropdownOpen(!genreDropdownOpen)}
                                        className="w-full input-field flex items-center justify-between text-left"
                                    >
                                        <span className={metadata.genre.length === 0 ? 'text-gray-400' : 'text-white'}>
                                            {metadata.genre.length === 0 ? 'Select genres...' : `${metadata.genre.length} genre${metadata.genre.length !== 1 ? 's' : ''} selected`}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${genreDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Genre dropdown options */}
                                    {genreDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                                            {genres.map((genre) => (
                                                <button
                                                    key={genre}
                                                    type="button"
                                                    onClick={() => toggleGenre(genre)}
                                                    className="w-full px-3 py-2 text-left text-white hover:bg-gray-700 transition-colors duration-200 flex items-center justify-between"
                                                >
                                                    <span>{genre}</span>
                                                    {metadata.genre.includes(genre) && (
                                                        <Check className="w-4 h-4 text-blue-400" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Year */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Year
                                    </label>
                                    <input
                                        type="number"
                                        value={metadata.year}
                                        onChange={(e) => handleMetadataChange('year', e.target.value)}
                                        className="input-field"
                                        placeholder={String(new Date().getFullYear())}
                                        min="1900"
                                        max={new Date().getFullYear()}
                                    />
                                </div>

                            </div>

                            {/* Access Control */}
                            <PrivacySettings
                                value={metadata.accessLevel}
                                password={metadata.accessPassword}
                                passwordHint={metadata.accessHint}
                                isPaid={metadata.isPaid}
                                price={metadata.price}
                                currency={metadata.currency}
                                onChange={(v) => handleMetadataChange('accessLevel', v)}
                                onPasswordChange={(v) => handleMetadataChange('accessPassword', v)}
                                onHintChange={(v) => handleMetadataChange('accessHint', v)}
                                onIsPaidChange={(v) => handleMetadataChange('isPaid', v)}
                                onPriceChange={(v) => handleMetadataChange('price', v)}
                                onCurrencyChange={(v) => handleMetadataChange('currency', v)}
                                disabled={loading}
                            />

                            {/* Copyright */}
                            <CopyrightSettings
                                isCopyrighted={metadata.isCopyrighted}
                                copyrightText={metadata.copyrightText}
                                onIsCopyrightedChange={(v) => handleMetadataChange('isCopyrighted', v)}
                                onCopyrightTextChange={(v) => handleMetadataChange('copyrightText', v)}
                                disabled={loading}
                            />

                            {/* Contributing Artists */}
                            <ContributingArtistSelector
                                selectedArtists={contributingArtists}
                                onArtistsChange={setContributingArtists}
                                placeholder="Search for artists who contributed to this track..."
                                maxArtists={10}
                                disabled={loading}
                            />

                            {/* Description / Lyrics */}
                            <div>
                                <div className="flex items-center gap-1 mb-2 border-b border-gray-700">
                                    <button
                                        type="button"
                                        onClick={() => setDescriptionTab('description')}
                                        className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                            descriptionTab === 'description'
                                                ? 'text-blue-400 border-blue-400'
                                                : 'text-gray-400 border-transparent hover:text-gray-300'
                                        }`}
                                    >
                                        Description
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDescriptionTab('lyrics')}
                                        className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                            descriptionTab === 'lyrics'
                                                ? 'text-blue-400 border-blue-400'
                                                : 'text-gray-400 border-transparent hover:text-gray-300'
                                        }`}
                                    >
                                        Lyrics
                                    </button>
                                </div>
                                {descriptionTab === 'description' ? (
                                    <textarea
                                        value={metadata.description}
                                        onChange={(e) => handleMetadataChange('description', e.target.value)}
                                        className="input-field min-h-[100px] resize-vertical"
                                        placeholder="Tell people about your track..."
                                        rows={4}
                                    />
                                ) : (
                                    <textarea
                                        value={metadata.lyrics}
                                        onChange={(e) => handleMetadataChange('lyrics', e.target.value)}
                                        className="input-field min-h-[100px] resize-vertical"
                                        placeholder="Add the full lyrics here..."
                                        rows={4}
                                    />
                                )}
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    value={metadata.tags}
                                    onChange={(e) => handleMetadataChange('tags', e.target.value)}
                                    className="input-field"
                                    placeholder="chill, lofi, acoustic (separate with commas)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Add tags to help people find your track (separate with commas)
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                                <button
                                    onClick={() => setStep(1)}
                                    className="btn-secondary"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!metadata.title.trim() || loading || (metadata.isCopyrighted && !metadata.copyrightText.trim())}
                                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save Track
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MusicUploadModal;